import { Box, Container, Dialog, DialogContent, Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CachePolicies } from "use-http";

import { useGetDetector } from "api/services/useDetector";
import { UserLimitModal } from "components/account/UserLimitModal/UserLimitModal";
import { SkeletonIndicator } from "components/common/AnimatedWebcam/SkeletonIndicator";
import { ErrorView } from "components/common/ErrorView/ErrorView";
import { getErrorTypeFromStatus } from "components/common/ErrorView/utils";
import { ExpirePlanBar } from "components/common/ExpirePlanBar/ExpirePlanBar";
import { TerminationNoticeBar } from "components/common/TerminationNoticeBar/TerminationNoticeBar";
import { AddImageQueryModal } from "components/detector/AddImageQueryModal/AddImageQueryModal";
import { ActionOptions, DetectorActionModal } from "components/detector/DetectorActionModal";
import { DetectorDetailAnswers } from "components/detector/DetectorDetailAnswers";
import { DetectorEnabledSwitch } from "components/detector/DetectorEnabledSwitch";
import { BinaryTopLevelStats } from "components/detector/DetectorStats/TopLevelStats/BinaryTopLevelStats";
import { BoundingBoxesTopLevelStats } from "components/detector/DetectorStats/TopLevelStats/BoundingBoxesTopLevelStats";
import { CountTopLevelStats } from "components/detector/DetectorStats/TopLevelStats/CountTopLevelStats";
import { MultichoiceTopLevelStats } from "components/detector/DetectorStats/TopLevelStats/MultichoiceTopLevelStats";
import { TextRecognitionTopLevelStats } from "components/detector/DetectorStats/TopLevelStats/TextRecognitionTopLevelStats";
import { DetectorSubHeader } from "components/detector/DetectorSubHeader";
import { ExampleImage } from "components/detector/ExampleImage";
import { HeaderInput } from "components/detector/HeaderInput";
import { ImageQueryPreview, PageContext } from "components/detector/ImageQueryPreview";
import { QueryEditWarningModal } from "components/detector/QueryEditWarningModal";
import { OpenDisagreementButton } from "components/disagreements";
import { JANZU_URL } from "config/config";
import { DETECTOR_DETAILS_REFRESH_TIME } from "config/constants";
import DetectorDetailSettings from "features/detectorDetailSettings/DetectorDetailSettings";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useDetectorContext } from "hooks/useDetectorContext";
import { useFetchCustom } from "hooks/useFetchCustom";
import { useModalLimit } from "hooks/useModalLimit/useModalLimit";
import { useRefreshCount } from "hooks/useRefreshCount/useRefreshCount";
import { GroundlightButton } from "new-components/buttons/GroundlightButton";
import { Typography } from "new-components/typography/Typography";
import { isFetchingDetector } from "store/slices/DetectorPage/detectorSlice";
import { setIsFocusModalOpen } from "store/slices/FocusModal/focusModalSlice";
import { DetectorModes } from "types/createDetector";
import { DEFAULT_CONFIDENCE_THRESHOLD, Detector } from "types/detectors";
import { User, UserProps } from "types/users";

import { DetectorDetailsOnboardingModal } from "./components/DetectorDetailsOnboardingModal";

interface DetectorDetailProps {
  detectorId: string;
  user: User;
}

export type FilterOptsArray = {
  column: string;
  value: string;
}[];

export interface Annotation {
  id?: string;
  text: string;
}

export interface EditableDetectorFields {
  confidence_threshold: number;
  set: {
    id: string;
    name: string;
  };
}

const TopLevelStatsComponents = {
  [DetectorModes.BINARY]: BinaryTopLevelStats,
  [DetectorModes.COUNT]: CountTopLevelStats,
  [DetectorModes.MULTICHOICE]: MultichoiceTopLevelStats,
  [DetectorModes.TEXT_RECOGNITION]: TextRecognitionTopLevelStats,
  [DetectorModes.BOUNDING_BOX]: BoundingBoxesTopLevelStats,
};

export function DetectorDetailRoute(props: UserProps) {
  const { user } = props;
  const { id } = useParams<{ id?: string }>();

  return id ? <DetectorDetail detectorId={id} user={user} /> : <Box>Please provide an id in the URL!</Box>;
}

export function DetectorDetail(props: DetectorDetailProps) {
  const { detectorId, user } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { isBlocked, modalLimit, handleOpenModalLimit, handleCloseModalLimit } = useModalLimit("iqIsBlocked");
  const [actionType, setActionType] = useState(ActionOptions.Rename);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [committedConfidenceThreshold, setCommittedConfidenceThreshold] = useState(DEFAULT_CONFIDENCE_THRESHOLD);
  const [confirmIsOpen, setConfirmIsOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [createLabelPopoverOpen, setCreateLabelPopoverOpen] = useState(false);
  const [detector, setDetector] = useState<Detector | null>();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [queryInputIsDisabled, setQueryInputIsDisabled] = useState(true);
  const [queryText, setQueryText] = useState("");
  const [editWarningDialogOpen, setEditWarningDialogOpen] = useState<boolean>(false);
  const queryInputRef = useRef<HTMLInputElement>(null);

  const detectorDataLoading = useAppSelector(isFetchingDetector);
  const dispatch = useAppDispatch();

  const menuIsOpen = Boolean(anchorEl);
  const lastImageQuery = useMemo(() => (detector?.last_image_query ? [detector?.last_image_query] : []), [detector]);

  const { setCurrentDetectorId } = useDetectorContext();
  const { count, reachRefreshTime } = useRefreshCount(DETECTOR_DETAILS_REFRESH_TIME);

  // TODO: BE needs to allow other methods beside get for this endpoint
  const oldUrl = `${JANZU_URL}device-api/predictors/${detectorId}`;

  const posichecksUrl = `${JANZU_URL}device-api/v1/image-queries?detector_id=${detectorId}&want_async=true`;
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null); // State to store backend error messages
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [failedFiles, setFailedFiles] = useState<File[]>([]);
  const onClose = () => {
    setOpen(false);
    setError(null); // Reset error state on close
    setLoading(false); // Reset loading state on close
    setFailedFiles([]); // Reset failed files on close
  };

  const {
    patch: updateDetector,
    del: deleteDetector,
    loading: isDetectorUpdating,
  } = useFetchCustom<Detector>(oldUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const {
    data: detectorFetchData,
    error: fetchDetectorError = undefined,
    refetch: getDetector,
    isLoading: isDetectorQueryLoading,
    response,
  } = useGetDetector(detectorId, {
    // refetch the detector every 10 seconds, this already uses the useRefreshCount hook but it can be done here as well
    refetchInterval: count >= DETECTOR_DETAILS_REFRESH_TIME && !detectorDataLoading ? 1000 : false,
  });

  const detectorMode = detectorFetchData?.mode;

  const navigate = useNavigate();

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openActionDialog = useCallback(
    (type: ActionOptions) => {
      setActionType(type);
      if (type === "Edit") {
        setEditWarningDialogOpen(true);
        setQueryInputIsDisabled(false);
        handleMenuClose();
      } else {
        setDialogIsOpen(true);
      }
    },
    [handleMenuClose]
  );

  const handleEditWarningDialogClose = () => {
    setEditWarningDialogOpen(false);
    // setTimeout is required in order to work as expected (focus on edit query input)
    setTimeout(() => {
      queryInputRef.current?.focus();
    }, 0);
  };

  const handleDiscardChanges = useCallback(() => {
    setQueryInputIsDisabled(true);
    setQueryText(detector?.label_prompt || "");
  }, [detector?.label_prompt]);

  const handleSaveChanges = useCallback(() => {
    setActionType(ActionOptions.Edit);
    setDialogIsOpen(true);
  }, []);

  const queryHasChanged = useCallback(() => queryText !== detector?.label_prompt, [queryText, detector?.label_prompt]);

  const resetUrl = `${JANZU_URL}device-api/predictor-reset/${detectorId}`;
  const { del: reset } = useFetchCustom<Detector>(resetUrl, { cachePolicy: CachePolicies.NO_CACHE });
  const [loadingPosichecks, setLoadingPosichecks] = useState(false);

  useEffect(() => {
    if (count >= DETECTOR_DETAILS_REFRESH_TIME && !detectorDataLoading) {
      getDetector();
    }
  }, [count, detector?.last_image_query, detectorDataLoading, getDetector]);

  useEffect(() => {
    if (!isDetectorQueryLoading && detectorFetchData && !fetchDetectorError && queryInputIsDisabled) {
      setDetector(detectorFetchData);
      setCurrentDetectorId(detectorFetchData.id);
      if (detectorFetchData.confidence_threshold !== committedConfidenceThreshold) {
        setCommittedConfidenceThreshold(detectorFetchData.confidence_threshold);
      }
      setQueryText(detectorFetchData.label_prompt);
    } else if (!isDetectorQueryLoading && fetchDetectorError) {
      setDetector(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectorFetchData, fetchDetectorError, setCurrentDetectorId, queryInputIsDisabled]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (confirmIsOpen) {
      // TODO: Close the confirm dialog based on when the selected action is done instead of a timer
      const timer = window.setTimeout(() => {
        // If necessary, could add a ref to only set to false if true after the timeout
        setQueryInputIsDisabled(true);
        setConfirmIsOpen(false);
      }, 3000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [confirmIsOpen]);

  const doPatch = async (field: string, value: string) => {
    const patchPromise = updateDetector({ [field]: value });
    patchPromise.then(() => {
      getDetector().then(() => {
        setQueryInputIsDisabled(true);
      });
    });
  };

  const doDelete = () => {
    const deletePromise = deleteDetector();

    // Delaying the redirect to detectors page a couple of seconds for a smoother transition
    deletePromise.then(() => {
      setTimeout(() => {
        navigate("/reef/detectors");
      }, 2000);
    });
  };

  const doReset = () => {
    const resetPromise = reset();
    resetPromise.then(() => {
      // This will reach the count threshold and trigger a refresh of the data of the detector
      // This help to avoid seeing the old data after the reset and the refresh of the count.
      reachRefreshTime();
    });
  };

  const closeLabelPopover = useCallback(() => {
    setCreateLabelPopoverOpen(false);
    dispatch(setIsFocusModalOpen(false));
  }, [dispatch]);

  const handleRefetch = useCallback(async () => {
    setLoadingPosichecks(true);
    await getDetector();
    setLoadingPosichecks(false);
    closeLabelPopover();
  }, [setLoadingPosichecks, getDetector, closeLabelPopover]);

  const handleSave = async (files: File[]) => {
    if (!files[0]) {
      console.error("No file provided");
      return;
    }
    
    setLoading(true);
    setFailedFiles([]);
    setError(null);
    setUploadMessage(`Uploading ${files.length} file${files.length > 1 ? 's' : ''}...`);
    
    // Upload all files in parallel
    const results = await Promise.all(
      files.map(async (file) => {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const imageBinary = new Uint8Array(arrayBuffer);

          const response = await fetch(posichecksUrl, {
            method: "POST",
            headers: {
              "Content-Type": file.type,
            },
            body: imageBinary,
          });

          // Check for rate limiting (429)
          if (response.status === 429) {
            const retryAfter = response.headers.get("Retry-After");
            const waitMessage = retryAfter ? ` Please wait ${retryAfter} seconds.` : "";
            enqueueSnackbar(`Rate limit reached.${waitMessage}`, { variant: "warning" });
            return { success: false, file, isRateLimited: true };
          }

          const responseData = await response.json();

          if (response.ok && responseData?.id) {
            return { success: true, file };
          }
          
          const errorDetail = responseData?.detail || "Upload failed";
          enqueueSnackbar(`${file.name}: ${errorDetail}`, { variant: "error" });
          return { success: false, file };
        } catch (error: any) {
          console.error(`Error uploading ${file.name}:`, error);
          enqueueSnackbar(`Error uploading ${file.name}: ${error?.message || "Unknown error"}`, { variant: "error" });
          return { success: false, file };
        }
      })
    );

    const failed = results.filter((r) => !r.success);
    const successCount = results.filter((r) => r.success).length;
    const rateLimitedCount = failed.filter((r) => r.isRateLimited).length;
    
    setLoading(false);
    setUploadMessage(null);
    
    // Keep failed files staged for retry
    if (failed.length > 0) {
      setFailedFiles(failed.map((r) => r.file));
    }
    
    // Handle results based on success/failure counts
    if (failed.length === 0) {
      // All uploads succeeded - show success and auto-close
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setError(null);
        setOpen(false);
      }, 2000);
    } else if (rateLimitedCount > 0) {
      // Rate limited - show specific message
      const otherFailures = failed.length - rateLimitedCount;
      let message = `Rate limit reached. ${rateLimitedCount} file${rateLimitedCount !== 1 ? 's' : ''} could not be uploaded.`;
      if (otherFailures > 0) {
        message += ` ${otherFailures} other file${otherFailures !== 1 ? 's' : ''} also failed.`;
      }
      if (successCount > 0) {
        message += ` ${successCount} file${successCount !== 1 ? 's' : ''} uploaded successfully.`;
      }
      message += " Failed files are ready to retry.";
      setError(message);
    } else if (successCount > 0) {
      // Partial success - show error but keep modal open
      setError(`${failed.length} of ${files.length} uploads failed. Failed files are ready to retry.`);
    } else {
      // All failed
      setError(`All uploads failed. Files are ready to retry.`);
    }
  };

  // Automatically save the images as soon as they're dropped
  const handleModifyImage = async (files: File[]) => {
    await handleSave(files);
    reachRefreshTime();
  };
  const TopLevelStats = TopLevelStatsComponents[detector?.mode || DetectorModes.BINARY];

  const isDetectorLoading = !detectorFetchData;
  const errorType = getErrorTypeFromStatus(response?.status || 200);

  if (errorType) return <ErrorView error={errorType} />;

  const handleAddImage = () => {
    if (isBlocked) {
      handleOpenModalLimit();
      return;
    }

    setOpen(true);
  };

  return (
    <Box id="detector-detail">
      {detector ? (
        <>
          <DetectorSubHeader
            user={user}
            queryInputIsDisabled={queryInputIsDisabled}
            handleDiscardChanges={handleDiscardChanges}
            detector={detector}
            handleSaveChanges={handleSaveChanges}
            queryHasChanged={queryHasChanged}
            openActionDialog={openActionDialog}
            handleMenuClose={handleMenuClose}
            anchorEl={anchorEl}
            menuIsOpen={menuIsOpen}
            handleMenuOpen={handleMenuOpen}
          />
          <ExpirePlanBar />
          <TerminationNoticeBar />
        </>
      ) : (
        SkeletonIndicator({ loading: isDetectorLoading, variant: "rectangular", width: "100%", height: 50 })
      )}
      <Container>
        <Box id="detector-query" className="flex h-[50px]">
          {detector ? (
            <Box className="flex w-full h-full gap-5 border-hl-gray border-b-2 border-solid items-center">
              <HeaderInput
                label="Query:"
                value={queryText}
                disabled={queryInputIsDisabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQueryText(event.target.value)}
                valueClassName="text-primary-darker font-semibold"
                ref={queryInputRef}
              />
              {user?.show_disagreements && <OpenDisagreementButton detectorId={detectorId} />}
            </Box>
          ) : (
            SkeletonIndicator({ loading: isDetectorLoading, width: "100%", height: 30, className: "mt-[10px]" })
          )}
        </Box>
        <Stack className="w-full" direction={{ xs: "column", md: "row" }} gap={{ xs: "10px", sm: "20px", md: "30px" }}>
          {detector ? (
            <Box
              sx={{ width: { xs: "100%", md: "400px" } }}
              flexShrink={0}
              paddingTop={detectorMode === DetectorModes.BINARY ? "0px" : "50px"}
            >
              {detectorMode === DetectorModes.BINARY && <DetectorDetailAnswers />}
              <ExampleImage setCreateLabelPopoverOpen={setCreateLabelPopoverOpen} detector={detector} />
              <Stack direction="row" justifyContent="flex-end" className="mt-4" alignItems="center">
                <GroundlightButton variant="filled" onClick={handleAddImage} disabled={detector.status === "OFF"}>
                  <Typography>ADD IMAGE(S)</Typography>
                </GroundlightButton>
              </Stack>
              <DetectorDetailSettings
                getDetector={getDetector}
                detector={detector}
                detectorFetchData={detectorFetchData}
                fetchDetectorError={fetchDetectorError as unknown as Error}
                isDetectorUpdating={isDetectorUpdating}
                updateDetector={updateDetector}
                user={user}
              />
            </Box>
          ) : (
            SkeletonIndicator({ loading: isDetectorLoading, width: "100%", height: 1200 })
          )}
          {detector ? (
            <Stack className="w-full gap-4">
              {/* Demo detector cannot be disabled */}
              {!detector.is_explore_detector && (
                <Box className="w-full h-[52px] flex justify-end border-hl-gray border-b-2 border-solid">
                  <DetectorEnabledSwitch
                    detectorId={detector.id}
                    getDetector={getDetector}
                    acceptingNewQueriesValue={detector.status}
                  />
                </Box>
              )}
              <TopLevelStats
                detectorData={detector}
                confidenceThreshold={detectorFetchData?.confidence_threshold || DEFAULT_CONFIDENCE_THRESHOLD}
                count={count}
              />
              <ImageQueryPreview detector={detector} count={count} pageContext={PageContext.DetectorDetails} />
            </Stack>
          ) : (
            SkeletonIndicator({ loading: isDetectorLoading, width: "100%", height: 1200 })
          )}
        </Stack>
      </Container>

      {/* TODO: Double check all this dialogs are working as expected */}
      {detector && createLabelPopoverOpen && (
        <QueryDetailsModal
          loading={loadingPosichecks}
          getData={handleRefetch}
          rows={lastImageQuery}
          rowCount={1}
          selectedRowIndex={0}
          showDialog={createLabelPopoverOpen}
          onClose={closeLabelPopover}
          page={1}
          pageSize={1}
          detector={detector}
        />
      )}
      {detector && dialogIsOpen && (
        <DetectorActionModal
          key={detector.name}
          type={actionType}
          dialogIsOpen={dialogIsOpen}
          setDialogIsOpen={setDialogIsOpen}
          handleMenuClose={handleMenuClose}
          detectorName={detector.name}
          detectorGroupId={detector.set.id}
          setConfirmText={setConfirmText}
          setConfirmIsOpen={setConfirmIsOpen}
          queryText={queryText}
          doPatch={doPatch}
          doDelete={doDelete}
          doReset={doReset}
        />
      )}
      <QueryEditWarningModal dialogOpen={editWarningDialogOpen} onClose={handleEditWarningDialogClose} />
      <Dialog
        open={confirmIsOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setConfirmIsOpen(false);
        }}
        maxWidth="sm"
        PaperProps={{
          className: "bg-zinc-100",
        }}
      >
        <DialogContent className="m-4">
          <Typography variant="main" align="center">
            {confirmText}
          </Typography>
        </DialogContent>
      </Dialog>

      <AddImageQueryModal
        open={open}
        onClose={onClose}
        addImage={handleModifyImage}
        loading={loading}
        setLoading={setLoading}
        success={success}
        setError={setError}
        error={error}
        uploadMessage={uploadMessage}
        failedFiles={failedFiles}
      />
      <DetectorDetailsOnboardingModal />
      <UserLimitModal type="image-query" isModalOpen={modalLimit} handleCloseModal={handleCloseModalLimit} />
    </Box>
  );
}
